import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { CloudX } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { RESERVATIONS_BOOKINGS_PATH } from '@/pages/reservations/reservations.constants'

import DoughnutChart from '../../Charts/DoughnutChart/DoughnutChart'
import Icon from '../../Icons'
import StackBar from '../../Charts/HorizontalChart/StackBar'
import {
  getDiffColor,
  getPercentageDiffText,
  getAmenitiesChartData,
  getDiffDirectionSymbol,
} from '../helpers'
import UpsellingBlocker from '../UpsellingBlocker'
import Can from '../../Can'

const CHART_COLORS = [
  'rgba(255, 99, 132, 1)',
  '#EAE9E9',
  'rgba(153, 102, 255, 1)',
  'rgba(255, 159, 64, 1)',
]

const ReservationsAnalytics = ({ operationData }) => {
  const { t } = useTranslation(['building-analytics'])
  const [resertvationGraphActive, setResertvationGraphActive] = useState(true)
  const reservationsLabel = t('building-analytics:reservationsAnalytics.title')

  const defaultDoughnutChartData = operationData &&
    operationData.reservationsGraph && {
      labels: [
        t('building-analytics:reservationsAnalytics.refundedLabel'),
        t('building-analytics:reservationsAnalytics.cancelledLabel'),
        reservationsLabel,
        t('building-analytics:reservationsAnalytics.paidLabel'),
      ],
      datasets: [
        {
          data: [
            operationData.reservationsGraph.refundedTotal.value,
            operationData.reservationsGraph.cancelled.value,
            operationData.reservationsGraph.discounted.value,
            operationData.reservationsGraph.reservations.value,
          ],
          diff: [
            operationData.reservationsGraph.refundedTotal.percentageDiff,
            operationData.reservationsGraph.cancelled.percentageDiff,
            operationData.reservationsGraph.discounted.percentageDiff,
            operationData.reservationsGraph.reservations.percentageDiff,
          ],
          backgroundColor: CHART_COLORS,
          borderColor: CHART_COLORS,
          borderWidth: 1,
        },
      ],
    }

  const getColor = (dataToCheck) => {
    if (!dataToCheck || ['0.00', 'NaN'].includes(dataToCheck.percentageDiff)) return
    return dataToCheck.percentageDiff[0] === '-' ? '#cf2c21' : '#548671'
  }

  const defaultDoughnutChartOptions = operationData &&
    operationData.reservationsGraph && {
      plugins: {
        innerText: true,
        datalabels: {
          display: false,
        },
        doughnutlabel: {
          labels: [
            {
              text: operationData.reservationsGraph.totalReservations.value,
              font: {
                size: '25',
              },
              color: 'black',
            },
            {
              text: `${getPercentageDiffText(operationData.reservationsGraph.totalReservations)}`,
              font: {
                size: '15',
                weight: '500',
              },
              color: `${getColor(operationData.reservationsGraph.totalReservations)}`,
            },
          ],
        },
      },
    }

  const amenitiesReservationsLabel = t(
    'building-analytics:reservationsAnalytics.amenitiesReservationsLabel',
  )
  const amenitiesRevenueLabel = t('building-analytics:reservationsAnalytics.amenitiesRevenueLabel')

  const amenitiesReservationData = getAmenitiesChartData(
    { key: 'reservations', label: amenitiesReservationsLabel, color: '#7978EB' },
    operationData,
  )
  const amenitiesRevenueData = getAmenitiesChartData(
    { key: 'revenue', label: amenitiesRevenueLabel, color: '#BB5BE4' },
    operationData,
  )

  const isByAmenitiesShown =
    operationData &&
    operationData.amenities &&
    operationData.amenities.length &&
    amenitiesReservationData.datasets.length &&
    amenitiesRevenueData.datasets.length

  const totalReservations =
    !!operationData &&
    !!operationData.reservationsGraph &&
    operationData.reservationsGraph.totalReservations &&
    operationData.reservationsGraph.totalReservations.value

  const isReservationsTotalChartShown =
    !!operationData &&
    !!operationData.reservationsGraph &&
    defaultDoughnutChartData.datasets.length &&
    totalReservations

  const reservationsPending =
    !!operationData &&
    !!operationData.reservationsGraph &&
    operationData.reservationsGraph.reservationsPending

  const tooltipCallbacks = {
    label: (tooltipItem, data) => ` ${data.labels[tooltipItem['index']]}`,
    afterLabel: function (tooltipItem, data) {
      let dataset = data['datasets'][0]
      return ` ${dataset.data[tooltipItem['index']]} ${getDiffDirectionSymbol(
        dataset.diff[tooltipItem['index']],
      )} ${dataset.diff[tooltipItem['index']]}`
    },
  }

  return (
    <div className="operation-data__main-block__subblock">
      <div className="operation-data__main-block__subblock__header">
        <h3 className="reservations-title">{reservationsLabel}</h3>
        <Link className="redirect-button" to={RESERVATIONS_BOOKINGS_PATH}>
          {t('building-analytics:reservationsAnalytics.goToReservations')}
          <Icon
            styles={{ width: '20px', paddingLeft: '5px' }}
            viewBox="0 0 20 20"
            icon={'arrowRight'}
            svgProps={{ stroke: '#646464', strokeLinecap: 'round', strokeLinejoin: 'round' }}
            fill="none"
          />
        </Link>
      </div>
      <Can
        on={'Navbar'}
        to={'reservations'}
        feedFlag={'amenityReservations'}
        upsellingLink={
          <UpsellingBlocker
            type="amenityReservations"
            text={t('building-analytics:reservationsAnalytics.upsellingText')}
            wrapperClassName="operation-data__main-block__subblock__upselling-blocker"
          />
        }
      >
        <div className="operation-data__main-block__subblock__three-row-blocks">
          <div className="total-graph-wrapper">
            <h3 className="title">{t('building-analytics:reservationsAnalytics.total')}</h3>
            {reservationsPending ? (
              <div className="warning-wrapper">
                <Icon icon="exclamationPointTriangle" fill="#EE8E00" />
                <div>{reservationsPending}</div>
                <div>{t('building-analytics:common.pending')}</div>
                <Link className="redirect-button" to={RESERVATIONS_BOOKINGS_PATH}>
                  <span>{t('building-analytics:common.view')}</span>
                  <Icon
                    styles={{ width: '20px', paddingLeft: '5px' }}
                    viewBox="0 0 20 20"
                    icon={'arrowRight'}
                    svgProps={{
                      stroke: '#ee8e00',
                      strokeLinecap: 'round',
                      strokeLinejoin: 'round',
                    }}
                    fill="none"
                  />
                </Link>
              </div>
            ) : null}
            {isReservationsTotalChartShown ? (
              <div style={{ display: 'flex', width: '300px', justifyContent: 'center' }}>
                <DoughnutChart
                  options={defaultDoughnutChartOptions}
                  data={defaultDoughnutChartData}
                  tooltipCallbacks={tooltipCallbacks}
                  width={250}
                  height={250}
                />
              </div>
            ) : (
              <div className="no-data">
                <CloudX size={24} />
                <h3>{t('building-analytics:common.noDataTitle')}</h3>
                <p>{t('building-analytics:common.noDataDescription')}</p>
              </div>
            )}
          </div>
          <div
            className={`amenities-graph-wrapper ${
              operationData &&
              operationData.amenities &&
              operationData.amenities.length &&
              'amenities-padding-right'
            }`}
          >
            <h3 className="title">{t('building-analytics:reservationsAnalytics.byAmenities')}</h3>
            {isByAmenitiesShown ? (
              <React.Fragment>
                <button
                  className={`reservations-button ${
                    resertvationGraphActive && 'reservations-active'
                  }`}
                  onClick={() => setResertvationGraphActive(true)}
                >
                  {amenitiesReservationsLabel}
                </button>
                <button
                  onClick={() => setResertvationGraphActive(false)}
                  className={`revenue-button ${!resertvationGraphActive && 'revenue-active'}`}
                >
                  {amenitiesRevenueLabel}
                </button>
                <div style={resertvationGraphActive ? {} : { display: 'none' }}>
                  <StackBar
                    data={amenitiesReservationData}
                    height={
                      amenitiesReservationData.datasets[0].data.length <= 1
                        ? 40
                        : amenitiesReservationData.datasets[0].data.length * 30
                    }
                    wrapperStyles={{
                      height: '400px',
                      width: '300px',
                      maxHeight: '240px',
                      overflow: 'auto',
                    }}
                  />
                </div>
                <div style={resertvationGraphActive ? { display: 'none' } : {}}>
                  <StackBar
                    data={amenitiesRevenueData}
                    height={
                      amenitiesRevenueData.datasets[0].data.length <= 1
                        ? 40
                        : amenitiesRevenueData.datasets[0].data.length * 30
                    }
                    wrapperStyles={{
                      height: '400px',
                      width: '300px',
                      maxHeight: '240px',
                      overflow: 'auto',
                    }}
                  />
                </div>
              </React.Fragment>
            ) : (
              <div className="no-data">
                <CloudX size={24} />
                <h3>{t('building-analytics:common.noDataTitle')}</h3>
                <p>{t('building-analytics:common.noDataDescription')}</p>
              </div>
            )}
          </div>
          <div className="revenue-info-wrapper">
            <h3 className="title">{t('building-analytics:reservationsAnalytics.revenue')}</h3>
            {!!operationData &&
            !!operationData.reservationsRevenue &&
            (operationData.reservationsRevenue.netRevenue.value ||
              operationData.reservationsRevenue.grossRevenue.value ||
              operationData.reservationsRevenue.refunds.value) ? (
              <React.Fragment>
                <div className="subblock-wrapper">
                  <div className="subblock-line">
                    <span className="subblock-text">
                      {t('building-analytics:reservationsAnalytics.grossRevenue')}
                    </span>{' '}
                    <span className="subblock-bold-text">
                      ${operationData.reservationsRevenue.grossRevenue.value || '0'}
                    </span>
                    {operationData.reservationsRevenue.grossRevenue &&
                      operationData.reservationsRevenue.grossRevenue.percentageDiff !== '-' && (
                        <span
                          className={`subblock-small-text ${getDiffColor(
                            operationData.reservationsRevenue.grossRevenue,
                          )}`}
                        >
                          {getPercentageDiffText(operationData.reservationsRevenue.grossRevenue)}
                        </span>
                      )}
                  </div>
                  <div className="subblock-line">
                    <span className="subblock-text">
                      {t('building-analytics:reservationsAnalytics.refunds')}
                    </span>{' '}
                    <span className="subblock-bold-text">
                      ${operationData.reservationsRevenue.refunds.value || '0'}
                    </span>
                    {operationData.reservationsRevenue.refunds &&
                      operationData.reservationsRevenue.refunds.percentageDiff !== '-' && (
                        <span
                          className={`subblock-small-text ${getDiffColor(
                            operationData.reservationsRevenue.refunds,
                          )}`}
                        >
                          {getPercentageDiffText(operationData.reservationsRevenue.refunds)}
                        </span>
                      )}
                  </div>
                  <div className="subblock-line">
                    <span className="subblock-text">
                      {t('building-analytics:reservationsAnalytics.discounts')}
                    </span>{' '}
                    <span className="subblock-bold-text">
                      ${operationData.reservationsRevenue.discounts.value || '0'}
                    </span>
                    {operationData.reservationsRevenue.discounts &&
                      operationData.reservationsRevenue.discounts.percentageDiff !== '-' && (
                        <span
                          className={`subblock-small-text ${getDiffColor(
                            operationData.reservationsRevenue.discounts,
                          )}`}
                        >
                          {getPercentageDiffText(operationData.reservationsRevenue.discounts)}
                        </span>
                      )}
                  </div>
                </div>

                <span className="subblock-text">
                  {t('building-analytics:reservationsAnalytics.netRevenue')}
                </span>
                <div className="revenue-info-wrapper__bottom-wrapper">
                  <h3 className="revenue-info-wrapper__bottom-wrapper__number">
                    ${operationData.reservationsRevenue.netRevenue.value || '0'}
                  </h3>
                  {operationData.reservationsRevenue.netRevenue &&
                    operationData.reservationsRevenue.netRevenue.percentageDiff !== '-' && (
                      <div
                        className={`revenue-info-wrapper__bottom-wrapper__percent-wrapper ${getDiffColor(
                          operationData.reservationsRevenue.netRevenue,
                        )}`}
                      >
                        <span className="percent-text">
                          {getPercentageDiffText(operationData.reservationsRevenue.netRevenue)}
                        </span>
                      </div>
                    )}
                </div>
              </React.Fragment>
            ) : (
              <div className="no-data">
                <CloudX size={24} />
                <h3>{t('building-analytics:common.noDataTitle')}</h3>
                <p>{t('building-analytics:common.noDataDescription')}</p>
              </div>
            )}
          </div>
        </div>
      </Can>
    </div>
  )
}

ReservationsAnalytics.propTypes = {
  operationData: PropTypes.object,
}
export default ReservationsAnalytics
